import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiGlobe, FiEye, FiEyeOff, FiImage, FiExternalLink } from 'react-icons/fi';
import './HalamanPMB.css';

const HalamanPMB = () => {
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    page_slug: '',
    page_title: '',
    page_content: '',
    page_type: 'static',
    is_published: true,
    meta_description: '',
    banner_image: '',
    display_order: 0
  });
  const [selectedPage, setSelectedPage] = useState(null);
  const [previewPage, setPreviewPage] = useState(null);

  const pageTypeOptions = [
    { value: 'static', label: 'Halaman Statis' },
    { value: 'informasi', label: 'Informasi' },
    { value: 'pengumuman', label: 'Pengumuman' },
    { value: 'berita', label: 'Berita' },
    { value: 'prosedur', label: 'Prosedur Pendaftaran' }
  ];

  const fetchPages = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/pmb/halaman');
      console.log('Response data:', res.data);
      setPages(res.data.data);
    } catch (error) {
      console.error('Error fetching pages:', error);
      console.error('Error response:', error.response);
      toast.error('Gagal mengambil data halaman: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPages();
  }, []);

  const handleOpenModal = (page = null) => {
    if (page) {
      // Navigate to edit page with ID
      navigate(`/pmb/halaman/edit/${page._id}`);
    } else {
      // Navigate to create new page
      navigate('/pmb/halaman/new');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditMode(false);
    setSelectedPage(null);
  };

  const handleOpenPreview = (page) => {
    setPreviewPage(page);
    setShowPreviewModal(true);
  };

  const handleClosePreview = () => {
    setShowPreviewModal(false);
    setPreviewPage(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Auto-generate slug from title
    if (name === 'page_title' && !editMode) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      setFormData(prev => ({ ...prev, page_slug: slug }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.page_slug || !formData.page_title) {
      toast.error('Slug dan judul halaman wajib diisi');
      return;
    }

    try {
      if (editMode) {
        await axios.put(`/api/pmb/halaman/${selectedPage._id}`, formData);
        toast.success('Halaman berhasil diupdate');
      } else {
        await axios.post('/api/pmb/halaman', formData);
        toast.success('Halaman berhasil ditambahkan');
      }
      
      handleCloseModal();
      fetchPages();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Terjadi kesalahan');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus halaman ini?')) {
      try {
        await axios.delete(`/api/pmb/halaman/${id}`);
        toast.success('Halaman berhasil dihapus');
        fetchPages();
      } catch (error) {
        toast.error('Gagal menghapus halaman');
      }
    }
  };

  const togglePublish = async (id, currentStatus) => {
    try {
      await axios.patch(`/api/pmb/halaman/${id}/publish`, {
        is_published: !currentStatus
      });
      toast.success(`Halaman ${!currentStatus ? 'dipublikasikan' : 'disembunyikan'}`);
      fetchPages();
    } catch (error) {
      toast.error('Gagal mengubah status publikasi');
    }
  };

  const formatTanggal = (tanggal) => {
    return new Date(tanggal).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="spinner"></div>;
  }

  return (
    <div className="halaman-pmb-page">
      {/* Header */}
      <div className="modern-header">
        <div className="header-background">
          <div className="header-pattern"></div>
        </div>
        <div className="header-content">
          <div className="header-text">
            <div className="header-icon-wrapper">
              <FiGlobe className="header-icon" />
            </div>
            <div>
              <h1>Halaman PMB</h1>
              <p>Kelola konten dan halaman website PMB</p>
            </div>
          </div>
          <div className="header-actions">
            <button className="btn btn-glass" onClick={() => handleOpenModal()}>
              <FiPlus /> Tambah Halaman
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <FiGlobe />
          </div>
          <div className="stat-details">
            <h3>{pages.length}</h3>
            <p>Total Halaman</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
            <FiEye />
          </div>
          <div className="stat-details">
            <h3>{pages.filter(p => p.is_published).length}</h3>
            <p>Dipublikasikan</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>
            <FiEyeOff />
          </div>
          <div className="stat-details">
            <h3>{pages.filter(p => !p.is_published).length}</h3>
            <p>Draft</p>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="modern-table-container">
          <table className="modern-table">
            <thead>
              <tr>
                <th>Urutan</th>
                <th>Judul</th>
                <th>Slug</th>
                <th>Tipe</th>
                <th>Status</th>
                <th>Terakhir Diupdate</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {pages.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>
                    Tidak ada data halaman
                  </td>
                </tr>
              ) : (
                pages.sort((a, b) => a.display_order - b.display_order).map((page) => (
                  <tr key={page._id}>
                    <td>{page.display_order + 1}</td>
                    <td><strong>{page.page_title}</strong></td>
                    <td><code>/{page.page_slug}</code></td>
                    <td>
                      <span className="badge badge-info">
                        {pageTypeOptions.find(t => t.value === page.page_type)?.label || page.page_type}
                      </span>
                    </td>
                    <td>
                      <button
                        className={`toggle-btn ${page.is_published ? 'active' : ''}`}
                        onClick={() => togglePublish(page._id, page.is_published)}
                      >
                        {page.is_published ? <FiEye /> : <FiEyeOff />}
                        {page.is_published ? 'Publik' : 'Draft'}
                      </button>
                    </td>
                    <td>{formatTanggal(page.updatedAt)}</td>
                    <td>
                      <div className="table-actions">
                        <button
                          className="table-btn table-btn-view"
                          onClick={() => handleOpenPreview(page)}
                          title="Lihat Halaman"
                        >
                          <FiExternalLink />
                        </button>
                        <button
                          className="table-btn table-btn-edit"
                          onClick={() => handleOpenModal(page)}
                          title="Edit"
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          className="table-btn table-btn-delete"
                          onClick={() => handleDelete(page._id)}
                          title="Hapus"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Halaman' : 'Tambah Halaman Baru'}</h3>
              <button className="modal-close" onClick={handleCloseModal}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Judul Halaman <span className="required">*</span></label>
                    <input
                      type="text"
                      name="page_title"
                      value={formData.page_title}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Informasi Pendaftaran"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Slug URL <span className="required">*</span></label>
                    <input
                      type="text"
                      name="page_slug"
                      value={formData.page_slug}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="informasi-pendaftaran"
                      required
                    />
                    <small>URL: /pmb/{formData.page_slug}</small>
                  </div>

                  <div className="form-group">
                    <label>Tipe Halaman</label>
                    <select
                      name="page_type"
                      value={formData.page_type}
                      onChange={handleChange}
                      className="form-control"
                    >
                      {pageTypeOptions.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Urutan Tampil</label>
                    <input
                      type="number"
                      name="display_order"
                      value={formData.display_order}
                      onChange={handleChange}
                      className="form-control"
                      min="0"
                    />
                  </div>

                  <div className="form-group full-width">
                    <label>URL Banner/Gambar</label>
                    <div className="input-with-icon">
                      <FiImage />
                      <input
                        type="text"
                        name="banner_image"
                        value={formData.banner_image}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="https://example.com/banner.jpg"
                      />
                    </div>
                  </div>

                  <div className="form-group full-width">
                    <label>Meta Description (SEO)</label>
                    <textarea
                      name="meta_description"
                      value={formData.meta_description}
                      onChange={handleChange}
                      className="form-control"
                      rows="2"
                      placeholder="Deskripsi singkat untuk SEO (max 160 karakter)"
                      maxLength="160"
                    />
                    <small>{formData.meta_description.length}/160 karakter</small>
                  </div>

                  <div className="form-group full-width">
                    <label>Konten Halaman</label>
                    <textarea
                      name="page_content"
                      value={formData.page_content}
                      onChange={handleChange}
                      className="form-control"
                      rows="10"
                      placeholder="Tulis konten halaman disini... (Mendukung HTML)"
                    />
                    <small>Anda dapat menggunakan HTML untuk formatting</small>
                  </div>

                  <div className="form-group full-width">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="is_published"
                        checked={formData.is_published}
                        onChange={handleChange}
                      />
                      <span>Publikasikan Halaman</span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  <FiX /> Batal
                </button>
                <button type="submit" className="btn btn-primary">
                  <FiSave /> {editMode ? 'Update' : 'Simpan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && previewPage && (
        <div className="modal-overlay" onClick={handleClosePreview}>
          <div className="modal-content modal-preview" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="preview-header-info">
                <h3>{previewPage.page_title}</h3>
                <div className="preview-badges">
                  <span className={`badge ${previewPage.is_published ? 'badge-success' : 'badge-warning'}`}>
                    {previewPage.is_published ? 'Published' : 'Draft'}
                  </span>
                  <span className="badge badge-info">
                    {pageTypeOptions.find(t => t.value === previewPage.page_type)?.label}
                  </span>
                </div>
              </div>
              <button className="modal-close" onClick={handleClosePreview}>&times;</button>
            </div>
            
            <div className="modal-body preview-body">
              {/* Page Info */}
              <div className="preview-info-section">
                <div className="preview-info-grid">
                  <div className="preview-info-item">
                    <label>URL Slug:</label>
                    <code>/{previewPage.page_slug}</code>
                  </div>
                  <div className="preview-info-item">
                    <label>Urutan Tampil:</label>
                    <span>{previewPage.display_order + 1}</span>
                  </div>
                  <div className="preview-info-item">
                    <label>Jumlah Views:</label>
                    <span>{previewPage.views || 0} views</span>
                  </div>
                  <div className="preview-info-item">
                    <label>Terakhir Diupdate:</label>
                    <span>{formatTanggal(previewPage.updatedAt)}</span>
                  </div>
                </div>

                {previewPage.meta_description && (
                  <div className="preview-meta">
                    <label>Meta Description (SEO):</label>
                    <p className="meta-description">{previewPage.meta_description}</p>
                  </div>
                )}
              </div>

              {/* Banner Image */}
              {previewPage.banner_image && (
                <div className="preview-banner">
                  <label>Banner Image:</label>
                  <div className="banner-preview">
                    <img src={previewPage.banner_image} alt={previewPage.page_title} />
                  </div>
                </div>
              )}

              {/* Page Content */}
              <div className="preview-content-section">
                <label>Konten Halaman:</label>
                <div 
                  className="preview-content"
                  dangerouslySetInnerHTML={{ __html: previewPage.page_content }}
                />
              </div>

              {/* Created By */}
              {previewPage.created_by && (
                <div className="preview-footer-info">
                  <small>
                    Dibuat oleh: <strong>{previewPage.created_by.name || 'Admin'}</strong>
                  </small>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={handleClosePreview}
              >
                <FiX /> Tutup
              </button>
              <button 
                type="button" 
                className="btn btn-primary" 
                onClick={() => {
                  handleClosePreview();
                  handleOpenModal(previewPage);
                }}
              >
                <FiEdit2 /> Edit Halaman
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HalamanPMB;
